import type {MouseEventHandler, ReactElement} from 'react';
import React, {forwardRef, useCallback, useEffect, useMemo, useState} from 'react';
import {AiTextPromptBoxFooter} from '@Components/ai-text-prompt-box/components/ai-text-prompt-box-footer';
import {AiTextPromptBoxContent} from '@Components/ai-text-prompt-box/components/ai-text-prompt-box-content';
import {Panel, PanelHeader} from '@Components/panel';
import type {PanelHeaderActionProps} from '@Components/panel/components/panel-header-action';
import {IconSize} from '@Components/icon-v2/icon.types';
import {updatePanelSizeData} from '@Components/panel/panel-reducer';
import {PANEL_ID as aiTextMobilePanelId} from '@Panels/ai-text-prompt-box-panel/ai-text-prompt-box-panel.types';
import type {
  AiGeneratedContent,
  AiTextPromptBoxProps,
  ErrorType,
  InternationlizedPromptSuggestion,
  OneOfPromptOption,
  PromptBoxConfigOptionType,
  RecentlyGeneratedAiContent,
  SelectedValueType,
} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiPromptBoxVariant, AiTextErrorTypes, isMiniVariant} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import type {UsePromptConfigOptionsType} from '@Hooks/ai-prompt-box/usePromptConfigOptions';
import usePromptConfigOptions from '@Hooks/ai-prompt-box/usePromptConfigOptions';
import {
  CONTENT_GENERATED_PROMPT_BOX_MODAL_HEIGHT,
  DEFAULT_PROMPT_BOX_MODAL_HEIGHT,
  handleErrorAndGetErrorMessage,
  isRecentAiGeneration,
  MONO_CONTENT_SELECTED_PROMPT_BOX_MODAL_HEIGHT,
  MULTI_CONTENT_SELECTED_PROMPT_BOX_MODAL_HEIGHT,
  MULTI_CONTENT_SELECTED_PROMPT_BOX_MODAL_HEIGHT_WITH_ERROR,
} from '@Libraries/ai-text-prompt-box-library';
import type {GenerateIdeasOriginator} from '@Libraries/ai-email-campaign-content-library';
import {useAppDispatch} from '@/hooks';
import styles from './ai-text-prompt-box.module.scss';
import type {VoidFunction} from '@Utils/general.util';
import {noop} from '@Utils/general.util';

const RECENT_CAPTIONS_MODE_BOX_HEIGHT = '462px';
const DEFAULT_MODE_BOX_HEIGHT_MONO_CONTENT = '310px';
const AI_CONTENT_MODE_HEIGHT_MULTI_CONTENT = '382px';
const ERROR_STATE_BOX_HEIGHT_MULTI_CONTENT = '410px';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const AiTextPromptBox = forwardRef<HTMLDivElement, AiTextPromptBoxProps>(({...props}, ref): ReactElement => {
  const {isInMobileMode = false, variant = AiPromptBoxVariant.MONOCONTENT} = props;
  const isMini = isMiniVariant(props);
  const showBackButton = props.showBackButton ?? true;

  const getDefaultPrompt = (): string => {
    if (isMini && props.defaultPrompt) {
      return props.defaultPrompt;
    }

    return '';
  };

  const getPromptConfigOptions = (): UsePromptConfigOptionsType => {
    return isMini ? usePromptConfigOptions(props.optionsToUse, props.defaultGenerationOptions) : usePromptConfigOptions(props.optionsToUse);
  };

  const [inRecentsMode, setInRecentsMode] = useState(false);
  const [prompt, setPrompt] = useState<string>(getDefaultPrompt());
  const [recents, setRecents] = useState<RecentlyGeneratedAiContent[]>([]);
  const [generations, setGenerations] = useState<AiGeneratedContent[]>([]);
  const [selectedContent, setSelectedContent] = useState<AiGeneratedContent | null>(null);
  const [isLoadingGenerate, setIsLoadingGenerate] = useState<boolean>(false);
  const [isLoadingRecents, setIsLoadingRecents] = useState<boolean>(false);
  const [isLoadingAlter, setIsLoadingAlter] = useState<boolean>(false);
  const [isLoadingUse, setIsLoadingUse] = useState<boolean>(false);
  const [dropdownsInUpdatedStateForAlterCaption, setDropdownsInUpdatedStateForAlterCaption] = useState<PromptBoxConfigOptionType[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const {selectedValues, getCachedValues, cachePromptOptionsUsedForGeneration, deleteCacheValues, defaultValues} = getPromptConfigOptions();

  const getCaptionsToShow = (): AiGeneratedContent[] | RecentlyGeneratedAiContent[] => {
    if (inRecentsMode) {
      return recents;
    }

    return generations;
  };

  const doesUserHaveRecents = recents.length > 0;
  const areCaptionsEmpty = generations.length < 1;
  const areRecentsEmpty = !doesUserHaveRecents;
  const areCaptionsToShowPresent = !!getCaptionsToShow().length;
  const areNoCaptionsPresentInMobileMode = isInMobileMode && !areCaptionsToShowPresent;
  const isMainScreen = areCaptionsEmpty && !inRecentsMode;
  const isContentSelected = !!selectedContent;
  const isError = !!errorMessage;
  const isErrorInMobileMode = isError && isInMobileMode;
  const isInGeneratedCaptionsMode = !areCaptionsEmpty && prompt;
  const shouldIncreaseHeight = (!areCaptionsEmpty || inRecentsMode) && !isContentSelected;
  const shouldShowRecentsButton = (doesUserHaveRecents || !areRecentsEmpty) && isMainScreen;
  const shouldFooterBeHidden = inRecentsMode && !isContentSelected;
  const shouldTheHeaderBeRenderedInsideTheContentBox = areCaptionsToShowPresent && !isInMobileMode && !isContentSelected;

  useEffect(() => {
    void fetchRecents();

    return (): void => {
      deleteCacheValues();
    };
  }, []);

  useEffect(() => {
    if (!selectedContent) return;

    const newOptions = getUpdatedOptions(getCachedValues() ?? defaultValues, selectedValues);
    const dropdownKeys = newOptions.map((option) => option.key);

    setDropdownsInUpdatedStateForAlterCaption((prevState) => {
      if (JSON.stringify(prevState) === JSON.stringify(dropdownKeys)) return prevState;
      return dropdownKeys;
    });
  }, [selectedValues, selectedContent, getCachedValues, defaultValues]);

  const handlePromptChange = useCallback(
    (updatedPrompt: string): void => {
      setPrompt((prevPrompt) => (prevPrompt !== updatedPrompt ? updatedPrompt : prevPrompt));
      props.onPromptChange?.(updatedPrompt);
    },
    [props]
  );

  const getUpdatedOptions = (previouslyUsedOptions: SelectedValueType, currentOptions: SelectedValueType): OneOfPromptOption[] => {
    const updatedKeys = Object.keys(currentOptions).filter((key) => {
      return !Object.prototype.hasOwnProperty.call(previouslyUsedOptions, key) || previouslyUsedOptions[key] !== currentOptions[key];
    });

    return updatedKeys.map((key) => {
      return {
        key,
        value: currentOptions[key],
      } as OneOfPromptOption;
    });
  };

  const getDefaultGenerationOptions = (): SelectedValueType => {
    return isMini && props.defaultGenerationOptions ? props.defaultGenerationOptions : getCachedValues();
  };

  const onContentClicked = useCallback((selectedContentFromList: AiGeneratedContent): void => {
    if (isRecentAiGeneration(selectedContentFromList)) {
      onRecentsClicked(selectedContentFromList);
    }

    setSelectedContent(selectedContentFromList);
  }, []);

  const fetchRecents = async (): Promise<void> => {
    if (isMiniVariant(props)) {
      return; // Mini variant does not support recents
    }

    resetError();
    setIsLoadingRecents(true);

    try {
      const aiContent = await props.getRecents();
      setRecents(aiContent);
    } catch (e) {
      handleError(e as ErrorType);
    } finally {
      setIsLoadingRecents(false);
    }
  };

  const generateNewIdeas = async (originator: GenerateIdeasOriginator): Promise<void> => {
    resetError();

    if (!prompt) {
      handleError({message: 'prompt not given', code: AiTextErrorTypes.NoPrompt, data: null, status: ''});
      return;
    }

    setIsLoadingGenerate(true);

    try {
      if (isMiniVariant(props)) {
        await props.rewriteContent(prompt, selectedValues);
      } else {
        const aiContent = await props.generateContent(prompt, selectedValues, originator);
        cachePromptOptionsUsedForGeneration();
        setGenerations([...aiContent, ...generations]);
      }
    } catch (e) {
      handleError(e as ErrorType);
    } finally {
      setIsLoadingGenerate(false);
    }
  };

  const onDoneClicked = (): void => {
    if (isMiniVariant(props)) {
      return; // mini variant does not support selection
    }

    resetError();

    if (!selectedContent?.text) {
      setErrorMessage('Unable to use generated content');
      return;
    }

    setIsLoadingUse(true);
    try {
      void props.useContent(prompt, selectedValues, selectedContent).then(fetchRecents);
      props.onContentSelected(selectedContent);
      props.onBack();
      reset();
    } catch (e) {
      handleError(e as ErrorType);
    } finally {
      setIsLoadingUse(false);
    }
  };

  const onRecentsClicked = (content: RecentlyGeneratedAiContent): void => {
    if (isMiniVariant(props)) {
      return; // mini variant does not support recents
    }

    resetError();

    if (!isRecentAiGeneration(content)) {
      throw new Error('Only recent captions can be reused');
    }

    setIsLoadingUse(true);
    try {
      void props.reuseContent(content.id);
    } catch (e) {
      handleError(e as ErrorType);
    } finally {
      setIsLoadingUse(false);
    }
  };

  const onAlterContent = async (): Promise<void> => {
    if (isMiniVariant(props)) {
      return; // mini variant does not support alteration
    }

    resetError();

    if (!selectedContent?.text) {
      handleError({message: 'No content selected', code: AiTextErrorTypes.NoSubject, data: null, status: ''});
      return;
    }

    setIsLoadingAlter(true);

    try {
      const response = await props.alterContent(selectedContent.text, selectedValues);
      cachePromptOptionsUsedForGeneration();
      setSelectedContent(response);
    } catch (e) {
      handleError(e as ErrorType);
    } finally {
      setIsLoadingAlter(false);
    }
  };

  const onGenerationOptionChange = (): ((changedProperty: OneOfPromptOption) => void) | undefined => {
    return isMini ? props.onGenerationOptionChange : undefined;
  };

  const resetError = useCallback((): void => {
    setErrorMessage(null);
  }, []);

  const resetSelectedContent = (): void => {
    setSelectedContent(null);
  };

  const resetPrompt = (): void => {
    setPrompt('');
  };

  const resetRecentsMode = (): void => {
    setInRecentsMode(false);
  };

  const reset = (): void => {
    resetError();
    resetPrompt();
    resetGeneration();
    resetSelectedContent();
    resetRecentsMode();
  };

  const handleError = (error: ErrorType): void => {
    const errorMessageContent = handleErrorAndGetErrorMessage(error);

    if (errorMessageContent) {
      setErrorMessage(errorMessageContent);
    }
  };

  const getModalHeightBasedOnPanelContentState = (): string => {
    if (isContentSelected) {
      switch (variant) {
        case AiPromptBoxVariant.MULTICONTENT:
          if (isError) {
            return MULTI_CONTENT_SELECTED_PROMPT_BOX_MODAL_HEIGHT_WITH_ERROR;
          }
          return MULTI_CONTENT_SELECTED_PROMPT_BOX_MODAL_HEIGHT;
        case AiPromptBoxVariant.MONOCONTENT:
        default:
          return MONO_CONTENT_SELECTED_PROMPT_BOX_MODAL_HEIGHT;
      }
    }

    if (areCaptionsToShowPresent) {
      return CONTENT_GENERATED_PROMPT_BOX_MODAL_HEIGHT;
    }

    if (isMainScreen) {
      return DEFAULT_PROMPT_BOX_MODAL_HEIGHT;
    }

    return DEFAULT_PROMPT_BOX_MODAL_HEIGHT;
  };

  useEffect(() => {
    if (!isInMobileMode) {
      return;
    }

    const heightOfModalInMobileMode = getModalHeightBasedOnPanelContentState();

    dispatch(
      updatePanelSizeData({
        sizeDataId: aiTextMobilePanelId,
        sizeData: {height: heightOfModalInMobileMode},
      })
    );
  }, [isInMobileMode, areRecentsEmpty, areCaptionsEmpty, isContentSelected, areCaptionsToShowPresent, isError]);

  const onClickRecents: MouseEventHandler = (e): void => {
    e.stopPropagation();
    e.preventDefault();
    resetError();
    setInRecentsMode(true);
  };

  const getCurrentPromptBoxTitle = (): string => {
    if (isMiniVariant(props)) {
      return ''; // mini variant does not support titles
    }

    if (isContentSelected) {
      return props.titles.selected;
    }

    if (inRecentsMode) {
      return props.titles.recents;
    }

    if (isMainScreen) {
      return props.titles.main;
    }

    if (isInGeneratedCaptionsMode) {
      return window.i18next.t('pmwjs_ideas_for_prompt', {prompt});
    }

    return '';
  };

  const resetGeneration = (): void => {
    setGenerations([]);
  };

  const backActionBasedOnCurrentState: MouseEventHandler | VoidFunction = (e): void => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (isMiniVariant(props)) {
      return; // mini variant does not support the back button
    }

    if (isContentSelected) {
      if (isError) {
        resetError();
        return;
      }
      setSelectedContent(null);
    } else if (inRecentsMode) {
      setInRecentsMode(false);
    } else if (isInGeneratedCaptionsMode) {
      resetGeneration();
    } else if (isMainScreen) {
      if (isErrorInMobileMode) {
        resetError();
        return;
      }
      props.onBack();
    } else {
      resetGeneration();
      setInRecentsMode(false);
    }
  };

  const renderMobileFooter = (): ReactElement => {
    if (isMainScreen) {
      return wrapWithPaddedDiv(renderFooter(), 4);
    }

    if (isContentSelected) {
      return wrapWithPaddedDiv(renderFooter(), 4);
    }

    return renderFooter();
  };

  const renderMobileContent = (): ReactElement => {
    return renderContent();
  };

  const recentButtonAction = useMemo((): PanelHeaderActionProps[] => {
    if (!shouldShowRecentsButton) {
      return [];
    }

    return [
      {
        key: 'ai-text-prompt-box-recents-action-mobile',
        icon: 'icon-history',
        iconSize: IconSize.SIZE_ICON_20,
        onClick: onClickRecents,
        className: shouldShowRecentsButton ? `size-icon-20 -square ${styles.padding10px} ${isLoadingGenerate ? styles.disabledHeaderAction : ''}` : '_hidden',
      },
    ];
  }, [areRecentsEmpty, shouldShowRecentsButton]);

  const wrapWithPaddedDiv = (children: ReactElement, spacingLevel: number): ReactElement => {
    return <div className={`spacing-p-${spacingLevel}`}>{children}</div>;
  };

  const renderHeader = (isRenderedInsideContentBox = false): ReactElement | null => {
    if (!isRenderedInsideContentBox && shouldTheHeaderBeRenderedInsideTheContentBox) {
      return null;
    }

    return (
      <PanelHeader
        backButtonIconSize={props.isOpenedInPopOver ? IconSize.SIZE_ICON_16 : undefined}
        className={`_borderless ${props.isOpenedInPopOver ? 'spacing-p-l-4 spacing-p-r-4' : ''}`}
        showCloseButton={!!props.onClose}
        onClose={props.onClose}
        showBackButton={inRecentsMode ? true : showBackButton}
        title={getCurrentPromptBoxTitle()}
        titleClasses={isMainScreen ? styles.aiTitle : styles.title}
        onBackButton={backActionBasedOnCurrentState}
        actions={
          shouldShowRecentsButton
            ? [
                {
                  key: 'ai-text-prompt-box-recents-action',
                  text: window.i18next.t('pmwjs_recents'),
                  icon: 'icon-history',
                  className: isLoadingGenerate ? styles.disabledHeaderAction : undefined,
                  iconSize: IconSize.SIZE_ICON_16,
                  onClick: isLoadingGenerate ? noop : onClickRecents,
                  title: window.i18next.t('pmwjs_recent'),
                },
              ]
            : undefined
        }
      />
    );
  };

  const renderMobileHeader = (): ReactElement => {
    return (
      <PanelHeader
        titleClasses={isMainScreen ? styles.aiTitle : undefined}
        title={getCurrentPromptBoxTitle()}
        onBackButton={backActionBasedOnCurrentState}
        actions={recentButtonAction}
        showCloseButton={!isMainScreen}
        onClose={props.onBack}
        showBackButton
        isOpenedInModal
        forceCenterAlignTitleWithActionItems
        backButtonIconSize={props.isOpenedInPopOver ? IconSize.SIZE_ICON_16 : undefined}
        className={props.isOpenedInPopOver ? 'spacing-p-l-4 spacing-p-r-4' : ''}
      />
    );
  };

  const renderContent = (): ReactElement => {
    let suggestions: InternationlizedPromptSuggestion[] = [];

    if (!isMiniVariant(props)) {
      suggestions = props.internationalizedPromptSuggestions ?? [];
    }

    return (
      <AiTextPromptBoxContent
        className={props.contentClassName}
        contentClassName={props.innerContentClassName}
        variant={variant}
        content={getCaptionsToShow()}
        titles={props.titles}
        optionsToUse={props.optionsToUse}
        isInMobileMode={isInMobileMode}
        isHidden={areNoCaptionsPresentInMobileMode}
        onSelectContent={onContentClicked}
        selectedContent={selectedContent}
        isLoadingGenerations={isLoadingGenerate}
        isLoadingAlteration={isLoadingAlter}
        isLoadingRecents={isLoadingRecents}
        isLoadingUseage={isLoadingUse}
        errorMessage={errorMessage}
        dropdownInUpdatedState={dropdownsInUpdatedStateForAlterCaption}
        alterationLoadingTexts={props.alterationLoadingTexts}
        promptSuggestions={suggestions}
        onPromptSuggestionClicked={handlePromptChange}
        prompt={prompt}
        {...(shouldTheHeaderBeRenderedInsideTheContentBox ? {header: renderHeader(true)} : null)}
      />
    );
  };

  const renderFooter = (): ReactElement => {
    return (
      <AiTextPromptBoxFooter
        variant={variant}
        prompt={prompt}
        setPrompt={handlePromptChange}
        onSubmitClick={generateNewIdeas}
        onStartOver={backActionBasedOnCurrentState}
        onDoneClick={onDoneClicked}
        onUpdateClick={onAlterContent}
        isInMobileMode={isInMobileMode}
        isLoadingAlter={isLoadingAlter}
        isLoadingUsage={isLoadingUse}
        isLoadingGenerate={isLoadingGenerate}
        isHidden={shouldFooterBeHidden}
        areCaptionsLoaded={!areCaptionsEmpty}
        optionsToUse={props.optionsToUse}
        selectedContent={selectedContent}
        defaultGenerationOptions={getDefaultGenerationOptions()}
        dropdownInUpdatedState={dropdownsInUpdatedStateForAlterCaption}
        placeholder={props.mainPlaceholder}
        errorMessage={errorMessage}
        recent={recents}
        onGenerationOptionChange={onGenerationOptionChange()}
      />
    );
  };

  const renderPanel = (): ReactElement => {
    return <Panel ref={ref} className={styles.mobileMode} panelHeader={renderMobileHeader()} panelContent={renderMobileContent()} panelFooter={renderMobileFooter()} />;
  };

  const getClassesForPromptBoxContainer = (): string => {
    return `
      flexbox 
      flex-column 
      ${styles.promtBox} 
      ${areCaptionsToShowPresent && !isContentSelected ? styles.hideOverflow : ''}
    `;
  };

  const getDynamicHeightForVariant = (): string => {
    switch (variant) {
      case AiPromptBoxVariant.MULTICONTENT:
        if (shouldIncreaseHeight) {
          return RECENT_CAPTIONS_MODE_BOX_HEIGHT;
        }

        if (isError && isContentSelected) {
          return ERROR_STATE_BOX_HEIGHT_MULTI_CONTENT;
        }

        return AI_CONTENT_MODE_HEIGHT_MULTI_CONTENT;
      case AiPromptBoxVariant.MONOCONTENT:
      default:
        if (shouldIncreaseHeight) {
          return RECENT_CAPTIONS_MODE_BOX_HEIGHT;
        }

        return DEFAULT_MODE_BOX_HEIGHT_MONO_CONTENT;
    }
  };

  const renderDefaultPromptBoxNormalMode = (): ReactElement => {
    return (
      <div ref={ref} className={`${styles.promptboxContainer} ai-caption-prompt-box ${props.promptboxContainerClass ?? ''}`}>
        <div className={`${getClassesForPromptBoxContainer()} ${props.sectionsContainerClass ?? ''}`} style={{height: getDynamicHeightForVariant()}}>
          {renderHeader()}
          {renderContent()}
          {renderFooter()}
        </div>
      </div>
    );
  };

  const renderDefaultPromptBox = (): ReactElement => {
    return isInMobileMode ? renderPanel() : renderDefaultPromptBoxNormalMode();
  };

  const renderMiniPromptBox = (): ReactElement => {
    return wrapWithPaddedDiv(renderFooter(), 3);
  };

  switch (variant) {
    case AiPromptBoxVariant.MINIATURE:
      return renderMiniPromptBox();
    default:
      return renderDefaultPromptBox();
  }
});
